<template>
  <div id="threadModal" class="offcanvas-background">
    <div class="offcanvas offcanvas-right"
       id="threadModal"
       v-click-outside="closeAll">
    <!--begin::Header-->
    <div class="offcanvas-header d-flex align-items-center justify-content-between pb-4">
      <div class="d-flex align-items-center">
        <inline-svg :src="require('@/assets/images/icons/Icon_Topic.svg')"
                    width="40" height="40"
                    class="w-40px mr-3" alt="topic-icon"/>
        <span class="font-weight-light" style="font-size: 2.5rem">
             {{ thread.title }}
          </span>
      </div>
      <span class="btn btn-xs btn-icon btn-hover-primary ml-auto"
            @click="close">
        <inline-svg :src="require('@/assets/images/icons/Icon_Cancel.svg')"
                    width="20" height="20"/>
      </span>
    </div>
    <!--end::Header-->
    <!--begin::Content-->
    <div ref="threadDisplay" class="offcanvas-content h-100 pb-8">
      <ToggleButton class="mb-5 px-1 py-1"
        :defaultState="thread.followed"
        :labelEnableText="$t('forum.followed.thread')"
        :labelDisableText="$t('forum.unfollowed.thread')"
        v-on:change="followThread"/>
      <div class="resources-list">
        <div v-for="(post, index) in thread.posts" :key="post.id"
             class="flex flex-wrap">
          <div class="mb-3">
            <div class="d-flex justify-content-between mb-2">
              <div class="d-flex">
                <div class="user-info__image rounded-circle position-relative mr-2">
                  <div class="avatar-placeholder w-30px h-30px" style="font-size: 14px">
                    {{ formatPrivacyLevel(post.createdBy,3)}}
                  </div>
                </div>
                <div class="d-flex flex-column">
                    <span class="font-weight-bold">
                      {{ formatPrivacyLevel(post.createdBy,forum.privacyLevel)}}
                    </span>
                  <small class="opacity-75">{{ formatDateFull(post.updatedAt) }}</small>
                </div>
              </div>
              <div>
                <button
                  class="btn btn-forum-action btn-sm px-1 mb-2"
                  @click="quoteMessage($event)">
                  <inline-svg :src="require('@/assets/images/icons/Icon_Topic.svg')"
                              class="w-15px"
                              style="pointer-events: none"
                              width="15" height="15"/>
                </button>
                <button
                  v-if="post.createdByEmail.toLowerCase().trim()
                  === currentUser.login.toLowerCase().trim()"
                  class="btn btn-forum-action btn-sm px-1 mb-2"
                  @click="editMessage(post.id,$event)">
                  <inline-svg :src="require('@/assets/images/icons/Icon_Edit.svg')"
                              class="w-15px"
                              style="pointer-events: none"
                              width="15" height="15"/>
                </button>
                <button
                  v-if="post.createdByEmail.toLowerCase().trim()
                  === currentUser.login.toLowerCase().trim() && index !== 0"
                  class="btn btn-forum-action btn-sm px-1 mb-2"
                  @click="deleteMessage(post.id)">
                  <inline-svg :src="require('@/assets/images/icons/Icon_Delete.svg')"
                              class="w-15px"
                              style="pointer-events: none"
                              width="15" height="15"/>
                </button>
              </div>
            </div>
            <p class="forum-message font-weight-normal mb-3" v-html="post.content"></p>
            <div :class="{'end': index === thread.posts.length - 1}"
                 class="forum-separator"></div>
          </div>
        </div>
      </div>
      <div class="">
        <h3 class="font-weight-light mb-4">
          {{$t('forum.write.message')}}
        </h3>
        <TinyMCE id="postEditor"
                 type="text"
                 class="form-control form-control-sm"
                 style="height: 60vh"
                 :init="{
                   height: 200,
                   menubar: false,
                   statusbar: false,
                   contextmenu: false,
                   skin_url:'/js/skins/ui/oxide-dark',
                   content_css: '/js/skins/content/dark/content.css',
                   plugins: ['lists link paste image emoticons'],
                   toolbar:
                     'undo redo |' +
                      'bold italic underline blockquote |' +
                       'emoticons |' +
                        'numlist bullist |' +
                         'link image',
                   setup:function(ed) {
                     ed.on('Paste Change input Undo Redo', function(e) {
                       onMessageChange(ed.getContent());
                     });
                   }
                 }"
                 required>
        </TinyMCE>
        <div
          v-if="canPost"
          class="d-flex justify-content-between mt-5">
          <button
            class="btn btn-outline-primary btn-sm text-uppercase mb-2 mx-auto"
            @click="resetMessage()"
          >
            {{$t('app.button.cancel')}}
          </button>
          <button
            class="btn btn-primary btn-sm text-uppercase mb-2 mx-auto"
            @click="sendMessage()"
          >
            <span v-if="currentPost !== null">{{$t('forum.edit.message')}}</span>
            <span v-else>{{$t('forum.send.message')}}</span>
          </button>
        </div>
      </div>
    </div>
    <!--end::Content-->
  </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import HelperMixin from '@/mixins/helperMixin';
import 'tinymce/tinymce';
import TinyMCE from '@tinymce/tinymce-vue';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/plugins/paste';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/emoticons/js/emojis';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import { getTinymce } from '@tinymce/tinymce-vue/lib/cjs/main/ts/TinyMCE';
import store from '@/store';
import ToggleButton from '@/components/ui/ToggleButton.vue';
import { DELETE_THREAD_MESSAGE, SEND_THREAD_MESSAGE, FOLLOWING_THREAD } from '@/store/actions.type';

export default {
  name: 'ThreadCanvas',
  mixins: [HelperMixin],
  components: {
    TinyMCE,
    ToggleButton,
  },
  computed: {
    ...mapGetters(['currentUser', 'thread', 'forum', 'organization']),
  },
  mounted() {
    this.resetMessage();
  },
  data() {
    return {
      threadModal: true,
      currentPost: null,
      canPost: false,
      followed: store.getters.thread.followed,
    };
  },
  methods: {
    close() {
      this.resetMessage();
      if (document.getElementById('threadModal').classList.contains('offcanvas-on')) {
        this.$emit('close', { src: 'thread' });
      }
    },
    closeAll() {
      this.resetMessage();
      if (document.getElementById('threadModal').classList.contains('offcanvas-on')) {
        this.$emit('closeAll', { src: 'thread' });
      }
    },
    resetMessage() {
      this.currentPost = null;
      this.canPost = false;
      getTinymce().get('postEditor').setContent('');
    },
    onMessageChange(message) {
      if (message === '' && this.canPost) {
        this.canPost = false;
      } else if (!this.canPost) {
        this.canPost = true;
      }
    },
    sendMessage() {
      const messageContent = getTinymce().get('postEditor').getContent();
      if (messageContent === '') {
        this.$toasted.error(this.$t('help.modal.message.send.error'));
        return;
      }
      const forum = this.forum.id;
      const thread = this.thread.id;
      const post = this.currentPost;
      const organization = store.getters.organization.id;
      this.$store.dispatch(SEND_THREAD_MESSAGE,
        {
          messageContent,
          forum,
          organization,
          thread,
          post,
        }).then(() => {
        this.$toasted.success(this.$t('help.modal.message.send.success'));
        this.resetMessage();
        this.$Progress.finish();
      }).catch(() => {
        this.$toasted.error(this.$t('help.modal.message.send.error'));
      });
    },
    deleteMessage(postId) {
      this.$dialog.confirm(this.$t('forum.delete.confirm'), {
        okText: this.$t('forum.delete.confirm.ok'),
        cancelText: this.$t('app.button.cancel'),
      }).then(() => {
        const forum = this.forum.id;
        const thread = this.thread.id;
        const post = postId;
        const organization = store.getters.organization.id;
        this.$store.dispatch(DELETE_THREAD_MESSAGE,
          {
            forum,
            organization,
            thread,
            post,
          }).then(() => {
          this.$toasted.success(this.$t('help.modal.message.delete.success'));
          this.$Progress.finish();
        }).catch(() => {
          this.$toasted.error(this.$t('help.modal.message.delete.error'));
        });
      });
    },
    editMessage(postId, event) {
      this.currentPost = postId;
      const message = event.target.parentNode.parentNode.parentNode.getElementsByClassName('forum-message')[0].innerHTML;
      getTinymce().get('postEditor').setContent(message);
      const container = this.$refs.threadDisplay;
      container.scrollTop = container.scrollHeight;
    },
    quoteMessage(event) {
      const message = event.target.parentNode.parentNode.parentNode.getElementsByClassName('forum-message')[0].innerHTML;
      getTinymce().get('postEditor').setContent(`<blockquote>${message}</blockquote><p></p>`);
      const container = this.$refs.threadDisplay;
      container.scrollTop = container.scrollHeight;
    },
    followThread(value) {
      this.followed = value;
      const forum = this.forum.id;
      const thread = this.thread.id;
      const action = this.followed ? 'follow' : 'unfollow';
      const organization = store.getters.organization.id;
      this.$store.dispatch(FOLLOWING_THREAD,
        {
          forum,
          organization,
          thread,
          action,
        }).then(() => {
        const message = value ? this.$t('forum.follow.action') : this.$t('forum.unfollow.action');
        this.$toasted.success(message);
        this.$Progress.finish();
      }).catch(() => {
        this.$toasted.error(this.$t('forum.follow.action.error'));
      });
    },
  },
};
</script>
